import { Stack } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useState } from "react";
import { useGetPriorAuthStatusQuery } from "src/api/services/ePrescribeApi";
import { useGetPriorAuthQuery } from "src/api/services/priorAuthorizationsApi";
import { PatientProfile, Task, TaskType } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import ClinicalNote from "src/components/ClinicalNote";
import DosespotButton from "src/components/DosespotButton";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { usePrescriptionPrescribedSubscription } from "src/realtime-subscriptions/usePrescriptionPrescribedSubscription";
import { usePriorAuthInsertSubscription } from "src/realtime-subscriptions/usePriorAuthInsertSubscription";
import ErrorText from "src/shared/ErrorText";
import TaskWrapper from "src/wrappers/TaskWrapper";

interface Props {
  task: Task;
  onSuccess: () => Promise<void>;
  patient: PatientProfile;
}

const PriorAuthTask = ({ onSuccess, patient, task }: Props) => {
  const providerId = useClinician((c) => c.provider?.id);
  const { data: priorAuth } = useGetPriorAuthQuery(
    task.task_type === TaskType.PriorAuthUpdate
      ? Number(task.resource_id)
      : skipToken,
  );
  const { ePriorAuth, refetch } = useGetPriorAuthStatusQuery(
    priorAuth && priorAuth.case_id != null ? priorAuth.case_id : skipToken,
    {
      selectFromResult: ({ data }) => {
        return { ePriorAuth: data?.[0] ?? undefined };
      },
    },
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const errorLog = useSentryLogger("error");

  usePriorAuthInsertSubscription(providerId ?? null, task.task_type, onSuccess);
  usePrescriptionPrescribedSubscription(
    providerId ?? null,
    task.task_type,
    onSuccess,
  );

  const handleCompleteUpdatePA = useCallback(() => {
    if (ePriorAuth == null) return;

    setLoading(true);
    try {
      refetch();

      if (ePriorAuth.Status === "ActionRequired") {
        setError(
          "It seems like the prior authorization update request has not been address, please make sure the updates were submitted before moving forward",
        );
        return;
      }

      onSuccess();
      setError(null);
    } catch (err) {
      errorLog("Error completing task:", err);
    } finally {
      setLoading(false);
    }
  }, [ePriorAuth, errorLog, onSuccess, refetch]);

  return (
    <TaskWrapper task={task}>
      <Stack gap={2}>
        <DosespotButton patientId={patient.id} />
        {task.task_type === TaskType.PriorAuthUpdate ? (
          <LoadingButton
            variant="secondary"
            loading={loading}
            onClick={handleCompleteUpdatePA}
          >
            Complete task
          </LoadingButton>
        ) : null}
        {[TaskType.PriorAuthClosed, TaskType.PriorAuthCanceled].includes(
          task.task_type as TaskType,
        ) ? (
          <ClinicalNote
            backgroundColor="#ffffff"
            noteTitle="HEALTH NAVIGATOR"
            noteType="Coordinator"
            patient={patient}
            onSuccess={onSuccess}
          />
        ) : null}
        {error ? <ErrorText message={error} /> : null}
      </Stack>
    </TaskWrapper>
  );
};

export default PriorAuthTask;
